/**
 * Beta Dashboard Page (Admin Only)
 * 
 * Operational validation dashboard for NZ Beta launch.
 * Shows beta cohort metrics, no PII.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  BarChart3, Users, Activity, Shield, Bell, 
  TrendingUp, ArrowLeft, RefreshCw, Copy, Plus,
  Check, X, MessageSquare, Calendar, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BetaDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [newCode, setNewCode] = useState({
    code: '',
    max_uses: 10,
    expiration_days: 30,
    notes: '',
    beta_source: 'manual_invite'
  });
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchConfig, setBatchConfig] = useState({ count: 5, max_uses: 1, notes: '', expiration_days: 30, beta_source: 'manual_invite' });
  const [batchLoading, setBatchLoading] = useState(false);

  const isAdmin = !!user?.is_admin;

  const fetchDashboard = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoading(true);
      const [dashRes, codesRes, feedbackRes] = await Promise.all([
        api.beta.getDashboard(30),
        api.beta.listInviteCodes(true),
        api.beta.listFeedback(null, null, true)
      ]);

      if (dashRes.ok) {
        const data = await dashRes.json();
        setDashboardData(data);
      }

      if (codesRes.ok) {
        const data = await codesRes.json();
        setInviteCodes(data.codes || []);
      }

      if (feedbackRes.ok) {
        const data = await feedbackRes.json();
        setFeedback(data.feedback || []);
      }
    } catch (error) {
      console.error('Failed to fetch beta dashboard:', error);
      toast.error('Failed to load beta dashboard');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchDashboard();
  }, [isAdmin, navigate, fetchDashboard]);

  const handleCreateCode = async () => {
    try {
      const response = await api.beta.createInviteCode({
        code: newCode.code || null,
        max_uses: parseInt(newCode.max_uses) || 10,
        expiration_days: parseInt(newCode.expiration_days) || 30,
        notes: newCode.notes || null,
        beta_source: newCode.beta_source
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Invite code created: ${data.code}`);
        setShowCreateCode(false);
        setNewCode({ code: '', max_uses: 10, expiration_days: 30, notes: '', beta_source: 'manual_invite' });
        fetchDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create code');
      }
    } catch (error) {
      toast.error('Failed to create invite code');
    }
  };

  const handleDeactivateCode = async (codeId) => {
    try {
      const response = await api.beta.deactivateInviteCode(codeId);
      if (response.ok) {
        toast.success('Code deactivated');
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to deactivate code');
    }
  };

  const handleBatchCreate = async () => {
    setBatchLoading(true);
    try {
      const response = await api.beta.createBatchCodes(batchConfig);
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.count} codes created`);
        setShowBatchDialog(false);
        setBatchConfig({ count: 5, max_uses: 1, notes: '', expiration_days: 30, beta_source: 'manual_invite' });
        fetchDashboard();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to create batch');
      }
    } catch {
      toast.error('Failed to create batch codes');
    } finally {
      setBatchLoading(false);
    }
  };


  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf9f6] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#1a4d3e] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#1a4d3e]/70">Loading beta dashboard...</p>
        </div>
      </div>
    );
  }

  const metrics = dashboardData?.metrics || {};
  const summary = dashboardData?.summary || {};

  return (
    <div className="min-h-screen bg-[#faf9f6]">
      {/* Header */}
      <header className="bg-[#1a4d3e] text-white py-6 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-semibold">NZ Beta Dashboard</h1>
                <p className="text-white/70 text-sm">Operational validation metrics</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDashboard}
              className="border-white/30 text-white hover:bg-white/10"
              data-testid="refresh-dashboard"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-[#1a4d3e]/10">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-8">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'invites', label: 'Invite Codes', icon: Users },
              { id: 'feedback', label: 'Feedback', icon: MessageSquare },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#1a4d3e] text-[#1a4d3e]'
                    : 'border-transparent text-[#1a4d3e]/50 hover:text-[#1a4d3e]/70'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-[#1a4d3e]/10">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#1a4d3e]/10 rounded-lg">
                      <Users className="w-5 h-5 text-[#1a4d3e]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#1a4d3e]/60">Beta Households</p>
                      <p className="text-2xl font-bold text-[#1a4d3e]" data-testid="total-beta-households">
                        {summary.total_beta_households || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#1a4d3e]/10">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#1a4d3e]/10 rounded-lg">
                      <Activity className="w-5 h-5 text-[#1a4d3e]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#1a4d3e]/60">30-Day Retention</p>
                      <p className="text-2xl font-bold text-[#1a4d3e]" data-testid="retention-rate">
                        {metrics.retention_30_day?.rate || 0}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#1a4d3e]/10">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#1a4d3e]/10 rounded-lg">
                      <Check className="w-5 h-5 text-[#1a4d3e]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#1a4d3e]/60">Reconcile Rate</p>
                      <p className="text-2xl font-bold text-[#1a4d3e]" data-testid="reconcile-rate">
                        {metrics.weekly_reconcile_completion?.rate || 0}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#1a4d3e]/10">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#1a4d3e]/10 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-[#1a4d3e]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#1a4d3e]/60">Upgrade Interest</p>
                      <p className="text-2xl font-bold text-[#1a4d3e]" data-testid="upgrade-clicks">
                        {metrics.upgrade_interest_clicks?.total || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Engagement Metrics */}
              <Card className="border-[#1a4d3e]/10">
                <CardHeader>
                  <CardTitle className="text-[#1a4d3e]">Engagement Metrics</CardTitle>
                  <CardDescription>User behavior indicators</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-[#1a4d3e]/5">
                    <span className="text-[#1a4d3e]/70">Calm Mode Adoption</span>
                    <span className="font-semibold text-[#1a4d3e]" data-testid="calm-mode-rate">
                      {metrics.calm_mode_adoption?.rate || 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-[#1a4d3e]/5">
                    <span className="text-[#1a4d3e]/70">Bill Reminder Engagement</span>
                    <span className="font-semibold text-[#1a4d3e]" data-testid="bill-engagement-rate">
                      {metrics.bill_reminder_engagement?.rate || 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-[#1a4d3e]/70">Push-to-Action Conversion</span>
                    <span className="font-semibold text-[#1a4d3e]" data-testid="push-conversion-rate">
                      {metrics.push_to_action_conversion?.rate || 0}%
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Cohort by Source */}
              <Card className="border-[#1a4d3e]/10">
                <CardHeader>
                  <CardTitle className="text-[#1a4d3e]">Cohort by Source</CardTitle>
                  <CardDescription>Beta user acquisition channels</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(dashboardData?.cohort_by_source || {}).map(([source, count]) => (
                    <div key={source} className="flex justify-between items-center py-2 border-b border-[#1a4d3e]/5 last:border-0">
                      <span className="text-[#1a4d3e]/70 capitalize">{source.replace('_', ' ')}</span>
                      <Badge variant="secondary" className="bg-[#1a4d3e]/10 text-[#1a4d3e]">
                        {count}
                      </Badge>
                    </div>
                  ))}
                  {Object.keys(dashboardData?.cohort_by_source || {}).length === 0 && (
                    <p className="text-[#1a4d3e]/50 text-center py-4">No beta users yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Privacy Distribution */}
              <Card className="border-[#1a4d3e]/10">
                <CardHeader>
                  <CardTitle className="text-[#1a4d3e]">Privacy Mode Distribution</CardTitle>
                  <CardDescription>How beta users configure privacy</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(metrics.privacy_mode_distribution || {}).map(([mode, count]) => (
                    <div key={mode} className="flex justify-between items-center py-2 border-b border-[#1a4d3e]/5 last:border-0">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-[#1a4d3e]/50" />
                        <span className="text-[#1a4d3e]/70 capitalize">{mode.replace('_', ' ')}</span>
                      </div>
                      <Badge variant="secondary" className="bg-[#1a4d3e]/10 text-[#1a4d3e]">
                        {count}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Upgrade Interest by Source */}
              <Card className="border-[#1a4d3e]/10">
                <CardHeader>
                  <CardTitle className="text-[#1a4d3e]">Upgrade Interest by Source</CardTitle>
                  <CardDescription>Which channels show pricing interest</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(metrics.upgrade_interest_clicks?.by_source || {}).map(([source, count]) => (
                    <div key={source} className="flex justify-between items-center py-2 border-b border-[#1a4d3e]/5 last:border-0">
                      <span className="text-[#1a4d3e]/70 capitalize">{source.replace('_', ' ')}</span>
                      <Badge variant="secondary" className="bg-[#1a4d3e]/10 text-[#1a4d3e]">
                        {count} clicks
                      </Badge>
                    </div>
                  ))}
                  {Object.keys(metrics.upgrade_interest_clicks?.by_source || {}).length === 0 && (
                    <p className="text-[#1a4d3e]/50 text-center py-4">No upgrade clicks tracked</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'invites' && (
          <div className="space-y-6">
            {/* Create Code Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-[#1a4d3e]">Invite Codes</h2>
                <p className="text-[#1a4d3e]/60">Manage beta access codes</p>
              </div>
              <Dialog open={showCreateCode} onOpenChange={setShowCreateCode}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90" data-testid="create-invite-code-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Code
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create Invite Code</DialogTitle>
                    <DialogDescription>
                      Generate a new beta invite code for users or campaigns
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="code">Custom Code (optional)</Label>
                      <Input
                        id="code"
                        placeholder="e.g., MORTGAGE2025 (leave empty for random)"
                        value={newCode.code}
                        onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                        data-testid="invite-code-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="max_uses">Max Uses</Label>
                        <Input
                          id="max_uses"
                          type="number"
                          min="1"
                          max="1000"
                          value={newCode.max_uses}
                          onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                          data-testid="max-uses-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="expiration_days">Expires In (days)</Label>
                        <Input
                          id="expiration_days"
                          type="number"
                          min="1"
                          max="365"
                          value={newCode.expiration_days}
                          onChange={(e) => setNewCode({ ...newCode, expiration_days: e.target.value })}
                          data-testid="expiration-days-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="beta_source">Source Type</Label>
                      <Select 
                        value={newCode.beta_source} 
                        onValueChange={(value) => setNewCode({ ...newCode, beta_source: value })}
                      >
                        <SelectTrigger data-testid="source-select">
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual_invite">Manual Invite</SelectItem>
                          <SelectItem value="referral">Referral</SelectItem>
                          <SelectItem value="partner">Partner Campaign</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes / Label (optional)</Label>
                      <Input
                        id="notes"
                        placeholder="e.g., For mortgage broker campaign Q1"
                        value={newCode.notes}
                        onChange={(e) => setNewCode({ ...newCode, notes: e.target.value })}
                        data-testid="notes-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateCode(false)}>Cancel</Button>
                    <Button onClick={handleCreateCode} className="bg-[#1a4d3e]" data-testid="confirm-create-code">
                      Create Code
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Batch Generate */}
              <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-[#1a4d3e]/30 text-[#1a4d3e]" data-testid="batch-generate-btn">
                    Batch Generate
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Batch Generate Codes</DialogTitle>
                    <DialogDescription>Create multiple invite codes at once</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>How many?</Label>
                        <Select value={String(batchConfig.count)} onValueChange={(v) => setBatchConfig({ ...batchConfig, count: Number(v) })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="5">5 codes</SelectItem>
                            <SelectItem value="10">10 codes</SelectItem>
                            <SelectItem value="20">20 codes</SelectItem>
                            <SelectItem value="50">50 codes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Max uses each</Label>
                        <Input type="number" min="1" max="100" value={batchConfig.max_uses} onChange={(e) => setBatchConfig({ ...batchConfig, max_uses: Number(e.target.value) })} data-testid="batch-max-uses" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes / Label (optional)</Label>
                      <Input placeholder="e.g., Beta wave 2" value={batchConfig.notes} onChange={(e) => setBatchConfig({ ...batchConfig, notes: e.target.value })} data-testid="batch-notes" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowBatchDialog(false)}>Cancel</Button>
                    <Button onClick={handleBatchCreate} className="bg-[#1a4d3e]" disabled={batchLoading} data-testid="confirm-batch-create">
                      {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Generate ${batchConfig.count} Codes`}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Codes List */}
            <div className="grid gap-4">
              {inviteCodes.map((code) => (
                <Card key={code.code_id} className={`border-[#1a4d3e]/10 ${!code.is_active ? 'opacity-50' : ''}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <code className="text-lg font-mono font-bold text-[#1a4d3e]" data-testid={`code-${code.code_id}`}>
                            {code.code}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyCode(code.code)}
                            className="h-8 w-8 p-0"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <Badge variant={code.is_active ? "default" : "secondary"} className={code.is_active ? "bg-[#1a4d3e]" : ""}>
                          {code.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant="outline" className="capitalize">
                          {code.beta_source?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-[#1a4d3e]/60">
                        <span>Uses: {code.uses_count}/{code.max_uses}</span>
                        {code.expiration_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Expires: {new Date(code.expiration_date).toLocaleDateString()}
                          </span>
                        )}
                        {code.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivateCode(code.code_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`deactivate-${code.code_id}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Deactivate
                          </Button>
                        )}
                      </div>
                    </div>
                    {code.notes && (
                      <p className="mt-2 text-sm text-[#1a4d3e]/50">{code.notes}</p>
                    )}
                    {code.used_by?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#1a4d3e]/10">
                        <p className="text-xs font-medium text-[#1a4d3e]/60 mb-1.5">Used by:</p>
                        <div className="space-y-1">
                          {code.used_by.map((u, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-[#1a4d3e]/50">
                              <span className="font-mono">{u.email || u.user_id}</span>
                              {u.redeemed_at && (
                                <span className="text-[#1a4d3e]/30">{new Date(u.redeemed_at).toLocaleDateString()}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {inviteCodes.length === 0 && (
                <Card className="border-[#1a4d3e]/10">
                  <CardContent className="py-12 text-center">
                    <p className="text-[#1a4d3e]/50">No invite codes created yet</p>
                    <Button 
                      className="mt-4 bg-[#1a4d3e]" 
                      onClick={() => setShowCreateCode(true)}
                    >
                      Create Your First Code
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {activeTab === 'feedback' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-[#1a4d3e]">Beta Feedback</h2>
              <p className="text-[#1a4d3e]/60">User feedback from beta cohort — track response status</p>
            </div>

            <div className="grid gap-4">
              {feedback.map((fb) => (
                <Card key={fb.feedback_id} className="border-[#1a4d3e]/10">
                  <CardContent className="py-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge 
                          variant="outline" 
                          className={
                            fb.feedback_category === 'bug' ? 'border-red-500 text-red-600' :
                            fb.feedback_category === 'testimonial' ? 'border-green-500 text-green-600' :
                            fb.feedback_category === 'feature_request' ? 'border-blue-500 text-blue-600' :
                            'border-[#1a4d3e]/50 text-[#1a4d3e]'
                          }
                        >
                          {fb.feedback_category?.replace('_', ' ')}
                        </Badge>
                        <Select 
                          value={fb.response_status || 'new'} 
                          onValueChange={async (value) => {
                            try {
                              const response = await api.beta.updateFeedbackStatus(fb.feedback_id, value);
                              if (response.ok) {
                                toast.success('Status updated');
                                fetchDashboard();
                              }
                            } catch (error) {
                              toast.error('Failed to update status');
                            }
                          }}
                        >
                          <SelectTrigger className={`w-[130px] h-7 text-xs ${
                            fb.response_status === 'new' ? 'bg-amber-50 border-amber-300' :
                            fb.response_status === 'reviewing' ? 'bg-blue-50 border-blue-300' :
                            fb.response_status === 'responded' ? 'bg-purple-50 border-purple-300' :
                            fb.response_status === 'resolved' ? 'bg-green-50 border-green-300' :
                            'bg-gray-50'
                          }`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="reviewing">Reviewing</SelectItem>
                            <SelectItem value="responded">Responded</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                        {fb.is_beta_user && (
                          <Badge variant="outline" className="border-[#1a4d3e] text-[#1a4d3e]">Beta User</Badge>
                        )}
                        {fb.beta_source && (
                          <Badge variant="secondary" className="text-xs capitalize">
                            {fb.beta_source?.replace('_', ' ')}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-[#1a4d3e]/50">
                        {new Date(fb.created_at).toLocaleDateString()} {new Date(fb.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                    <p className="text-[#1a4d3e]/80 whitespace-pre-wrap">{fb.feedback_text}</p>
                    <div className="mt-3 flex items-center justify-between text-xs text-[#1a4d3e]/40">
                      <span>{fb.user_email}</span>
                      {fb.household_id && <span>Household: {fb.household_id}</span>}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {feedback.length === 0 && (
                <Card className="border-[#1a4d3e]/10">
                  <CardContent className="py-12 text-center">
                    <MessageSquare className="w-12 h-12 mx-auto text-[#1a4d3e]/20 mb-4" />
                    <p className="text-[#1a4d3e]/50">No feedback from beta users yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
